import * as THREE from "three";
import { createCollisionMaterial, buildMaterial } from "./materialFactory";
import { getSceneState } from "../stores/sceneStore";
import { acquireTexture as acquireTextureFromCache } from "../runtime/assetCache";
import { logger } from "../utils/logger";
import {
  getThreeGeomArgsFromMjcfSize,
  getThreeGeomScaleFromMjcfSize,
} from "../utils/mujocoGeomSizing";
import { createHeightFieldGeometry } from "../utils/terrainHeightFieldGeometry";

// Check if a geometry class is a collision geometry
export function isCollisionClass(className, classParentMap = {}) {
  let cur = className;
  while (cur) {
    if (cur === "collision") return true;
    cur = classParentMap[cur] ?? null;
  }
  return false;
}

// Get inherited geometry type from class hierarchy
export function getInheritedType(className, collisionTypeMap, classParentMap = {}) {
  let cur = className;
  while (cur) {
    if (collisionTypeMap && collisionTypeMap[cur]) {
      return collisionTypeMap[cur];
    }
    cur = classParentMap[cur] ?? null;
  }
  return null;
}

// Create collision geometry with proper material and visibility
export function createCollisionGeometry(
  geomPos,
  geomQuat,
  geomSize,
  geomType,
  showCollision,
  hfieldDef = null
) {
  // Validate input parameters
  if (!geomPos || geomPos.some((val) => isNaN(val) || val === undefined)) {
    logger.warn("Invalid geomPos:", geomPos);
    return null;
  }

  if (!geomSize || geomSize.some((val) => isNaN(val) || val === undefined)) {
    logger.warn("Invalid geomSize:", geomSize);
    return null;
  }

  if (!geomQuat || geomQuat.some((val) => isNaN(val) || val === undefined)) {
    logger.warn("Invalid geomQuat:", geomQuat);
    return null;
  }

  // Additional validation for geometry parameters
  if (geomSize.some((val) => val <= 0)) {
    logger.warn("Invalid geomSize - values must be positive:", geomSize);
    return null;
  }

  // Validate quaternion (should be normalized)
  const quatMagnitude = Math.sqrt(
    geomQuat[0] ** 2 + geomQuat[1] ** 2 + geomQuat[2] ** 2 + geomQuat[3] ** 2
  );
  if (Math.abs(quatMagnitude - 1) > 0.1) {
    logger.warn("Invalid quaternion - not normalized:", geomQuat, "magnitude:", quatMagnitude);
    return null;
  }

  const geomGroup = new THREE.Group();
  geomGroup.position.set(...geomPos);

  let geometry;
  switch (geomType) {
    case "box":
    case "rectangle":
      geometry = new THREE.BoxGeometry(...getThreeGeomArgsFromMjcfSize("box", geomSize));
      break;
    case "plane":
      geometry = new THREE.PlaneGeometry(...getThreeGeomArgsFromMjcfSize("plane", geomSize));
      break;
    case "hfield":
      geometry = createHeightFieldGeometry(geomSize, hfieldDef);
      break;
    case "sphere":
      geometry = new THREE.SphereGeometry(...getThreeGeomArgsFromMjcfSize("sphere", geomSize));
      break;
    case "ellipsoid":
      geometry = new THREE.SphereGeometry(...getThreeGeomArgsFromMjcfSize("ellipsoid", geomSize));
      break;
    case "cylinder":
      geometry = new THREE.CylinderGeometry(...getThreeGeomArgsFromMjcfSize("cylinder", geomSize));
      break;
    case "capsule":
      geometry = new THREE.CapsuleGeometry(...getThreeGeomArgsFromMjcfSize("capsule", geomSize));
      break;
    default:
      logger.warn(`Unsupported geometry type: ${geomType}`);
      return null;
  }

  const mesh = new THREE.Mesh(geometry, createCollisionMaterial());
  mesh.scale.set(...getThreeGeomScaleFromMjcfSize(geomType, geomSize));

  if (geomType === "capsule" || geomType === "cylinder") {
    // Rotate from Three.js Y-up to MuJoCo/editor local Z-up.
    mesh.rotation.set(0, Math.PI / 2, Math.PI / 2);
  } else if (geomType === "plane" || geomType === "hfield") {
    mesh.rotation.set(0, 0, 0);
  }
  geomGroup.add(mesh);
  // Now apply the geom's rotation to the group
  // Apply the geom's rotation to the group
  if (geomQuat && geomQuat.length === 4 && !geomQuat.some((val) => isNaN(val))) {
    const quaternion = new THREE.Quaternion(geomQuat[1], geomQuat[2], geomQuat[3], geomQuat[0]);
    quaternion.normalize();
    geomGroup.setRotationFromQuaternion(quaternion);
  } else {
    geomGroup.setRotationFromQuaternion(new THREE.Quaternion(0, 0, 0, 1));
  }
  geomGroup.visible = showCollision;

  return geomGroup;
}

export function createVisualGeometry(
  geomPos,
  geomQuat,
  geomSize,
  geomType,
  materialName,
  showVisual,
  materials,
  hfieldDef = null
) {
  // Validate input parameters
  if (!geomPos || geomPos.some((val) => isNaN(val) || val === undefined)) {
    logger.warn("Invalid geomPos:", geomPos);
    return null;
  }

  if (!geomSize || geomSize.some((val) => isNaN(val) || val === undefined)) {
    logger.warn("Invalid geomSize:", geomSize);
    return null;
  }

  if (!geomQuat || geomQuat.some((val) => isNaN(val) || val === undefined)) {
    logger.warn("Invalid geomQuat:", geomQuat);
    return null;
  }

  // Additional validation for geometry parameters
  if (geomSize.some((val) => val <= 0)) {
    logger.warn("Invalid geomSize - values must be positive:", geomSize);
    return null;
  }

  // Validate quaternion (should be normalized)
  const quatMagnitude = Math.sqrt(
    geomQuat[0] ** 2 + geomQuat[1] ** 2 + geomQuat[2] ** 2 + geomQuat[3] ** 2
  );
  if (Math.abs(quatMagnitude - 1) > 0.1) {
    logger.warn("Invalid quaternion - not normalized:", geomQuat, "magnitude:", quatMagnitude);
    return null;
  }

  const geomGroup = new THREE.Group();
  geomGroup.position.set(...geomPos);

  let geometry;
  switch (geomType) {
    case "box":
    case "rectangle":
      geometry = new THREE.BoxGeometry(...getThreeGeomArgsFromMjcfSize("box", geomSize));
      break;
    case "plane":
      geometry = new THREE.PlaneGeometry(...getThreeGeomArgsFromMjcfSize("plane", geomSize));
      break;
    case "hfield":
      geometry = createHeightFieldGeometry(geomSize, hfieldDef);
      break;
    case "sphere":
      geometry = new THREE.SphereGeometry(...getThreeGeomArgsFromMjcfSize("sphere", geomSize));
      break;
    case "ellipsoid":
      geometry = new THREE.SphereGeometry(...getThreeGeomArgsFromMjcfSize("ellipsoid", geomSize));
      break;
    case "cylinder":
      geometry = new THREE.CylinderGeometry(...getThreeGeomArgsFromMjcfSize("cylinder", geomSize));
      break;
    case "capsule":
      geometry = new THREE.CapsuleGeometry(...getThreeGeomArgsFromMjcfSize("capsule", geomSize));
      break;
    default:
      logger.warn(`Unsupported geometry type: ${geomType}`);
      return null;
  }

  // Get the material from the provided materials if materialName is provided
  let material;
  let resolvedMaterialUuid = null;
  if (materialName && materials) {
    const matData =
      materials[materialName] || Object.values(materials).find((mat) => mat?.name === materialName);
    if (matData) {
      const def = { ...matData, name: matData.name || materialName };
      resolvedMaterialUuid = matData.uuid || (materials[materialName] ? materialName : null);
      const texturesState = getSceneState().textures;
      const mapName = def.colorMap ?? null;
      const fingerprint = mapName ? texturesState[mapName]?.fingerprint || null : null;
      const resolver = () => null;
      material = buildMaterial(def.name, def, resolver);
      material.userData = {
        ...(material.userData || {}),
        materialUuid: resolvedMaterialUuid,
      };
      if (fingerprint) {
        try {
          acquireTextureFromCache(fingerprint)
            .then((tex) => {
              if (!material) return;
              material.map = tex.clone();
              material.userData = material.userData || {};
              material.userData.assetTextureId = fingerprint;
              material.needsUpdate = true;
            })
            .catch((error) => {
              logger.warn("[collisionVisualization] Failed to acquire colorMap texture", error);
            });
        } catch (error) {
          logger.warn("[collisionVisualization] Failed to start texture acquisition", error);
        }
      }
    }
  }

  // Fallback to a default material if no material found
  if (!material) {
    material = new THREE.MeshStandardMaterial({
      color: 0xcccccc,
      metalness: 0.1,
      roughness: 0.8,
    });
  }

  const mesh = new THREE.Mesh(geometry, material);
  mesh.scale.set(...getThreeGeomScaleFromMjcfSize(geomType, geomSize));
  mesh.userData = {
    ...(mesh.userData || {}),
    materialUuid: resolvedMaterialUuid,
  };

  if (geomType === "capsule" || geomType === "cylinder") {
    // Rotate from Three.js Y-up to MuJoCo/editor local Z-up.
    mesh.rotation.set(0, Math.PI / 2, Math.PI / 2);
  } else if (geomType === "plane" || geomType === "hfield") {
    mesh.rotation.set(0, 0, 0);
  }
  geomGroup.add(mesh);

  // Apply the geom's rotation to the group
  if (geomQuat && geomQuat.length === 4 && !geomQuat.some((val) => isNaN(val))) {
    const quaternion = new THREE.Quaternion(geomQuat[1], geomQuat[2], geomQuat[3], geomQuat[0]);
    quaternion.normalize();
    geomGroup.setRotationFromQuaternion(quaternion);
  } else {
    geomGroup.setRotationFromQuaternion(new THREE.Quaternion(0, 0, 0, 1));
  }
  geomGroup.visible = showVisual;

  return geomGroup;
}

// Update collision geometry visibility
export function updateCollisionVisibility(object, showCollision) {
  if (!object) return;

  // Robust: collision objects are explicitly tagged during scene build.
  // - Mesh collision geoms: `userData.type === "collision"` on the Mesh.
  // - Primitive collision geoms: `userData.type === "collision"` on the Group.
  //
  // Avoid relying on material colors, which can overlap with other helpers/visuals.
  if (object.userData && object.userData.type === "collision") {
    object.visible = !!showCollision;
    return;
  }
}

// Parse collision defaults from XML
export function parseCollisionDefaults(
  defaultElem,
  parentScale = null,
  classMap = {},
  typeMap = {},
  parentClass = null,
  parentMap = {}
) {
  let currentScale = parentScale;
  let currentType = null;
  const meshElem = Array.from(defaultElem.children).find((e) => e.tagName === "mesh");
  const geomElem = Array.from(defaultElem.children).find((e) => e.tagName === "geom");
  if (meshElem && meshElem.getAttribute("scale")) {
    currentScale = meshElem.getAttribute("scale").trim().split(/\s+/).map(Number);
  }
  if (geomElem && geomElem.getAttribute("type")) {
    currentType = geomElem.getAttribute("type");
  }
  const className = defaultElem.getAttribute("class");
  if (className && currentScale) {
    classMap[className] = currentScale;
  }
  if (className && currentType) {
    typeMap[className] = currentType;
  }
  if (className && parentClass) {
    parentMap[className] = parentClass;
  }
  Array.from(defaultElem.children).forEach((child) => {
    if (child.tagName === "default") {
      parseCollisionDefaults(child, currentScale, classMap, typeMap, className, parentMap);
    }
  });
  return { classMap, typeMap, parentMap };
}
